import {noop} from 'lodash';
import {useMemo, useCallback, useState, useEffect, Fragment} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Dropdown, DropdownItem, Icon, UserInitials, OnlineStatusIcon, LanguageSwitcher, Divider, OnlineStatusSwitcher,
    Toggle, ContactSupportModal
} from 'components';
import {firebase} from 'config/firebase';
import {ROUTE_PATH} from 'constants/routes';
import {history} from 'context/history';
import {LogoutType} from 'enums';
import {useObservable} from 'hooks';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {authDataStore, socketStore} from 'services';
import {OnlineStatus} from 'types';
import {userAuthUtils} from 'utils';
import {getParticipantName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.PROFILE_MENU;
const T_KEYS_CONNECTION = TRANSLATION_KEYS.COMMON.MESSAGES.CONNECTION;

type Props = {
    isAvailable: boolean;
    notificationsEnabled: boolean;
    onAvailabilityChange?: (value: boolean) => void;
    onNotificationsEnabledChange?: (value: boolean) => void;
    items?: {
        profile?: boolean;
        onlineStatus?: boolean;
        language?: boolean;
        notifications?: boolean;
        logout?: boolean;
        aboutMe?: boolean;
        support?: boolean;
    }
};

export const ProfileMenu: React.FC<Props> = ({
    isAvailable, notificationsEnabled, onAvailabilityChange = noop, onNotificationsEnabledChange = noop,
    items = {profile: true, onlineStatus: true, language: true, notifications: true, logout: true, support: true, aboutMe: true}
}: Props) => {
    const {connected: socketConnected} = useObservable(socketStore);
    const {localUser} = useObservable(authDataStore);
    const {t} = useTranslation();

    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [languageSelectorOpen, setLanguageSelectorOpen] = useState(false);
    const [statusSelectorOpen, setStatusSelectorOpen] = useState(false);
    const [firebaseSupported, setFirebaseSupported] = useState(false);
    const [notificationPermission, setNotificationPermission] = useState(null);
    const [contactSupportModalOpen, setContactSupportModalOpen] = useState(false);

    const userName = useMemo(() => getParticipantName(localUser), [localUser]);
    const hasPersonalProfilePermissions = useMemo(() => userAuthUtils.getUserPermissions()?.account?.myProfile?.enabled, []);

    const onlineStatus = useMemo(() => {
        if (!socketConnected) {
            return OnlineStatus.Offline;
        } else if (isAvailable) {
            return OnlineStatus.Online;
        } else {
            return OnlineStatus.Away;
        }
    }, [socketConnected, isAvailable]);

    const handleProfileClick = useCallback(() => {
        if (hasPersonalProfilePermissions) {
            history.push(ROUTE_PATH.USER.ACCOUNT.MY_PROFILE);
            setIsProfileMenuOpen(false);
        }
    }, [hasPersonalProfilePermissions]);

    const openLanguageSelector = useCallback(() => {
        setLanguageSelectorOpen(true);
    }, []);

    const closeLanguageSelector = useCallback(() => {
        setLanguageSelectorOpen(false);
    }, []);

    const openStatusSelector = useCallback(() => {
        setStatusSelectorOpen(true);
    }, []);

    const closeStatusSelector = useCallback(() => {
        setStatusSelectorOpen(false);
    }, []);

    const setInitialFirebaseSupported = useCallback(async () => {
        setFirebaseSupported(await firebase.isSupported());
    }, []);

    const onDropdownOpen = useCallback(() => {
        if (firebaseSupported) {
            setNotificationPermission(Notification?.permission);
        }
        setIsProfileMenuOpen(true);
    }, [firebaseSupported]);

    const onDropdownClose = useCallback(() => {
        setIsProfileMenuOpen(false);
    }, []);

    const onAboutMeClick = useCallback(() => {
        history.push(ROUTE_PATH.USER.ABOUT.ME);
        setIsProfileMenuOpen(false);
    }, []);

    const onSupportClick = useCallback(() => {
        setContactSupportModalOpen(true);
        setIsProfileMenuOpen(false);
    }, []);

    const handleCloseSupportModal = useCallback(() => {
        setContactSupportModalOpen(false);
    }, []);

    const renderNotificationError = useCallback((message: string) => <div className='tw-text-sm tw-text-alert'>{message}</div>, []);

    useEffect(() => {
        setInitialFirebaseSupported();
    }, [setInitialFirebaseSupported]);

    return (
        <Fragment>
            <Dropdown
                open={isProfileMenuOpen}
                distance={15}
                hideOnClick={false}
                childrenClassName='tw-rounded-lg tw-w-64 tw-overflow-hidden'
                triggerNode={
                    <div className='tw-relative'>
                        <div className='tw-flex-center tw-cursor-pointer'>
                            <UserInitials user={localUser} />
                            <Icon name='chevron-down' size='xs' className='tw-ml-1' />
                        </div>
                        <div className='tw-flex tw-absolute tw-bottom-0 tw-left-0'>
                            {onlineStatus === OnlineStatus.Offline ?
                                <Icon name='alert-triangle' color='alert' size='sm' fill='white' /> :
                                <OnlineStatusIcon
                                    status={onlineStatus}
                                    tooltip={false}
                                    reverseAlign
                                    size='md'
                                    iconClassName='tw-border tw-border-light'
                                />}
                        </div>
                    </div>
                }
                testAttribute={{id: 'profile-menu-dropdown'}}
                onOpen={onDropdownOpen}
                onClose={onDropdownClose}
            >
                {items.profile &&
                    <DropdownItem
                        className='tw-flex tw-flex-col tw-items-start tw-text-left tw-bg-primary-dimmed tw-py-3'
                        selectable={hasPersonalProfilePermissions}
                        onClick={handleProfileClick}
                    >
                        <span className='tw-ts-h5-bold'>{userName}</span>
                        <span className='tw-text-default-dimmed tw-text-base'>{localUser.accountName}</span>
                        {onlineStatus === OnlineStatus.Offline &&
                            <span className='tw-text-base tw-text-alert'>{t(T_KEYS_CONNECTION.INTERRUPTED)}</span>}
                    </DropdownItem>}

                {items.onlineStatus &&
                    <DropdownItem
                        className='tw-flex tw-justify-between tw-items-center'
                        disabled={onlineStatus === OnlineStatus.Offline}
                        onClick={openStatusSelector}
                    >
                        <span className='tw-text-default-dimmed'>{t(T_KEYS.OPTION_TITLES.STATUS)}</span>
                        <OnlineStatusSwitcher
                            className='tw-rounded-lg tw-py-1 tw-text-dark'
                            dropdownOpen={statusSelectorOpen}
                            status={onlineStatus}
                            disabled={onlineStatus === OnlineStatus.Offline}
                            icon
                            onAvailabilityChange={onAvailabilityChange}
                            onDropdownClose={closeStatusSelector}
                        />
                    </DropdownItem>}

                {items.language &&
                    <DropdownItem className='tw-flex tw-justify-between tw-items-center' onClick={openLanguageSelector}>
                        <span className='tw-text-default-dimmed'>{t(T_KEYS.OPTION_TITLES.LANGUAGE)}</span>
                        <LanguageSwitcher
                            className='tw-rounded-lg tw-py-1 tw-text-dark'
                            variant='dropdown'
                            dropdownOpen={languageSelectorOpen}
                            icon
                            onDropdownClose={closeLanguageSelector}
                        />
                    </DropdownItem>}

                {items.notifications &&
                    <DropdownItem
                        className='tw-flex tw-justify-between tw-items-center'
                        onClick={() => onNotificationsEnabledChange(!notificationsEnabled)}
                    >
                        <div className='tw-text-default-dimmed tw-text-left'>
                            <div>{t(T_KEYS.OPTION_TITLES.NOTIFICATIONS)}</div>
                            {userAuthUtils.isFirebaseSupportedAuthSource() &&
                                <Fragment>
                                    {!firebaseSupported && renderNotificationError(t(T_KEYS.NOTIFICATIONS.NOT_SUPPORTED))}
                                    {notificationPermission === 'denied' &&
                                    renderNotificationError(t(T_KEYS.NOTIFICATIONS.PERMISSIONS_DENIED))}
                                </Fragment>}
                        </div>
                        <Toggle name='notification' size='small' checked={notificationsEnabled} />
                    </DropdownItem>}
                {items.aboutMe &&
                    <DropdownItem
                        className='tw-flex tw-text-dark'
                        onClick={onAboutMeClick}
                    >
                        <Icon name='info' thickness='md' color='default' className='tw-mr-3' />
                        {t(T_KEYS.OPTION_TITLES.ABOUT_ME)}
                    </DropdownItem>}

                {items.support &&
                    <DropdownItem
                        className='tw-flex tw-text-dark'
                        onClick={onSupportClick}
                    >
                        <Icon name='support' thickness='md' color='default' className='tw-mr-3' />
                        {t(T_KEYS.OPTION_TITLES.SUPPORT)}
                    </DropdownItem>}

                {items.logout &&
                    <Fragment>
                        <div className='tw-px-5 tw-py-1'><Divider /></div>
                        <DropdownItem
                            className='tw-flex tw-text-dark'
                            testAttribute={{id: 'profile-menu-logout-dropdown-item'}}
                            onClick={() => userAuthUtils.logout(LogoutType.Manually)}
                        >
                            <Icon name='log-out' className='tw-mr-3' />
                            {t(T_KEYS.OPTION_TITLES.LOGOUT)}
                        </DropdownItem>
                    </Fragment>}
            </Dropdown>
            {items.support && <ContactSupportModal open={contactSupportModalOpen} onClose={handleCloseSupportModal} />}
        </Fragment>
    );
};
